/**
 * Notification System
 * Provides comprehensive user feedback with Persian language support
 * Handles success, error, warning, and info notifications
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';
import {
  resolveNotificationMessage,
  resolveNotificationOptions,
} from '../../utils/localizeNotificationMessage';

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string | undefined;
  message: string;
  persianTitle?: string | undefined;
  persianMessage?: string | undefined;
  duration?: number | undefined;
  persistent?: boolean | undefined;
  actions?: NotificationAction[] | undefined;
  timestamp: number;
  context?: string | undefined;
  isExiting?: boolean | undefined;
}

export interface NotificationAction {
  label: string;
  persianLabel?: string;
  action: () => void;
  style?: 'primary' | 'secondary' | 'danger';
}

export interface NotificationOptions {
  title?: string;
  persianTitle?: string;
  persianMessage?: string;
  duration?: number;
  persistent?: boolean;
  actions?: NotificationAction[];
  context?: string;
}

interface NotificationSystemContextType {
  notifications: Notification[];
  showNotification: (type: Notification['type'], message: string, options?: NotificationOptions) => string;
  showSuccess: (message: string, options?: NotificationOptions) => string;
  showError: (message: string, options?: NotificationOptions) => string;
  showWarning: (message: string, options?: NotificationOptions) => string;
  showInfo: (message: string, options?: NotificationOptions) => string;
  dismissNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

const NotificationSystemContext = createContext<NotificationSystemContextType | null>(null);

interface NotificationSystemProps {
  children: React.ReactNode;
  maxNotifications?: number;
  defaultDuration?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

export const NotificationSystem: React.FC<NotificationSystemProps> = ({
  children,
  maxNotifications = 5,
  defaultDuration = 5000,
  position = 'top-center' // Default to top-center for enhanced UX
}) => {
  const { t: tAuth } = useTranslation('auth');
  const { t: tNotifications } = useTranslation('notifications');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [recentNotifications, setRecentNotifications] = useState<Set<string>>(new Set());

  const localize = useCallback(
    (text: string | undefined) =>
      text ? resolveNotificationMessage(text, tNotifications) : text,
    [tNotifications]
  );

  const localizeOptions = useCallback(
    (options: NotificationOptions = {}) =>
      resolveNotificationOptions(options, tNotifications) ?? options,
    [tNotifications]
  );

  const generateId = useCallback(() => {
    return `notification_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }, []);

  const showNotification = useCallback((
    type: Notification['type'],
    message: string,
    options: NotificationOptions = {}
  ): string => {
    // Debug logging
    if (import.meta.env.DEV) {
      console.log('[NotificationSystem] showNotification called:', { type, message, options });
    }

    // Validate that we have at least a message or title
    const hasMessage = message && message.trim().length > 0;
    const hasTitle = (options.title && options.title.trim().length > 0) ||
      (options.persianTitle && options.persianTitle.trim().length > 0);
    const hasPersianMessage = options.persianMessage && options.persianMessage.trim().length > 0;

    if (!hasMessage && !hasTitle && !hasPersianMessage) {
      console.warn('[NotificationSystem] Attempted to show notification with no content:', {
        type,
        message,
        options,
        stack: new Error().stack
      });
      return '';
    }

    const id = generateId();
    const resolvedOptions = localizeOptions(options);
    const resolvedMessage = localize(message);
    const notification: Notification = {
      id,
      type,
      message: hasMessage ? resolvedMessage : '',
      title: resolvedOptions.title ? localize(resolvedOptions.title) : resolvedOptions.title,
      persianTitle: undefined,
      persianMessage: undefined,
      duration: resolvedOptions.duration ?? defaultDuration,
      persistent: resolvedOptions.persistent ?? false,
      actions: resolvedOptions.actions,
      timestamp: Date.now(),
      context: resolvedOptions.context
    };

    // Create a unique key for this notification
    const notificationKey = `${type}_${resolvedMessage}_${resolvedOptions.title || ''}`;
    
    // Check if we've seen this notification recently (longer window for bot-link waits).
    const dedupeMs = resolvedOptions.context === 'channel-bot-link' ? 120_000 : 3_000;
    if (recentNotifications.has(notificationKey)) {
      console.log('[NotificationSystem] Preventing duplicate notification:', { type, message, title: options.title });
      return id;
    }

    // Add to recent notifications
    setRecentNotifications(prev => new Set([...prev, notificationKey]));
    
    // Remove from recent notifications after dedupe window
    setTimeout(() => {
      setRecentNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationKey);
        return newSet;
      });
    }, dedupeMs);

    setNotifications(prev => {
      const newNotifications = [notification, ...prev];
      // Keep only the most recent notifications
      return newNotifications.slice(0, maxNotifications);
    });

    // Auto-dismiss non-persistent notifications
    if (!notification.persistent && notification.duration != null && notification.duration > 0) {
      setTimeout(() => {
        dismissNotification(id);
      }, notification.duration);
    }

    return id;
  }, [generateId, defaultDuration, maxNotifications, localize, localizeOptions]);

  const showSuccess = useCallback((message: string, options?: NotificationOptions) => {
    return showNotification('success', message, {
      title: tNotifications('success'),
      duration: 4000,
      ...options
    });
  }, [showNotification, tNotifications]);

  const showError = useCallback((message: string, options?: NotificationOptions) => {
    return showNotification('error', message, {
      title: tNotifications('error'),
      duration: 10000,
      persistent: false,
      ...options
    });
  }, [showNotification, tNotifications]);

  const showWarning = useCallback((message: string, options?: NotificationOptions) => {
    return showNotification('warning', message, {
      title: tNotifications('warning'),
      duration: 8000,
      ...options
    });
  }, [showNotification, tNotifications]);

  const showInfo = useCallback((message: string, options?: NotificationOptions) => {
    return showNotification('info', message, {
      title: tNotifications('info'),
      ...options
    });
  }, [showNotification, tNotifications]);

  const dismissNotification = useCallback((id: string) => {
    // Find the notification and trigger exit animation
    setNotifications(prev => {
      const notification = prev.find(n => n.id === id);
      // If notification doesn't exist or is already exiting, don't do anything
      if (!notification || notification.isExiting) {
        return prev;
      }
      
      return prev.map(n =>
        n.id === id
          ? { ...n, isExiting: true }
          : n
      );
    });

    // Remove after animation completes
    setTimeout(() => {
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, 500);
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Listen for session expiration events
  useEffect(() => {
    const handleSessionExpired = (event: CustomEvent) => {
      if ((window as any).__intentionalLogoutInProgress) {
        return;
      }
      try {
        if (sessionStorage.getItem('skipSessionExpiredToast') === '1') {
          return;
        }
      } catch {
        /* ignore */
      }

      const { type = 'warning', persistent = false, duration = 8000, context } = event.detail ?? {};
      const isAdmin = String(context ?? '').includes('admin');
      const body =
        context === 'session_inactivity_expiration'
          ? tAuth('sessionInactiveExpired')
          : tAuth(isAdmin ? 'adminSessionExpiredMessage' : 'sessionExpiredDefault');

      showNotification(type, body, {
        title: tAuth(isAdmin ? 'adminSessionExpiredTitle' : 'sessionExpiredTitle'),
        persistent,
        duration,
        context: context ?? 'session_expiration'
      });
    };

    window.addEventListener('session:expired:notification', handleSessionExpired as EventListener);
    
    return () => {
      window.removeEventListener('session:expired:notification', handleSessionExpired as EventListener);
    };
  }, [showNotification, tAuth]);

  const contextValue: NotificationSystemContextType = {
    notifications,
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    dismissNotification,
    clearAllNotifications
  };

  return (
    <NotificationSystemContext.Provider value={contextValue}>
      {children}
      <NotificationContainer
        notifications={notifications}
        position={position}
        onDismiss={dismissNotification}
      />
    </NotificationSystemContext.Provider>
  );
};

/**
 * Notification Container
 * Renders notifications in the specified position
 */
interface NotificationContainerProps {
  notifications: Notification[];
  position: string;
  onDismiss: (id: string) => void;
}

const NotificationContainer: React.FC<NotificationContainerProps> = ({
  notifications,
  position,
  onDismiss
}) => {
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';

  if (notifications.length === 0) return null;

  const getPositionClasses = () => {
    switch (position) {
      case 'top-left':
        return 'top-4 left-4 sm:left-4';
      case 'top-center':
        return 'notification-mobile';
      case 'top-right':
        return 'top-4 right-4 sm:right-4';
      case 'bottom-left':
        return 'bottom-4 left-4 sm:left-4';
      case 'bottom-center':
        return 'bottom-4 left-4 right-4 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2 sm:max-w-md';
      case 'bottom-right':
        return 'bottom-4 right-4 sm:right-4';
      default:
        return 'notification-mobile'; // Default to mobile-optimized top-center
    }
  };

  /** Must render under `document.body`, not inside `#root`: Headless UI Dialog portals outside root with its own z-index, which would otherwise sit above all in-root stacking (blocking clicks on toasts). */
  const layer = (
    <div
      className={`fixed z-[2147483000] ${getPositionClasses()} safe-area-top`}
      dir="ltr"
      data-app-notification-layer
    >
      <div className="flex w-full flex-col space-y-3 sm:space-y-4 px-0 sm:px-4">
        {notifications.map((notification, index) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onDismiss={() => onDismiss(notification.id)}
            index={index}
            isRtl={isRtl}
          />
        ))}
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return layer;
  }
  return createPortal(layer, document.body);
};

/**
 * Individual Notification Item
 */
interface NotificationItemProps {
  notification: Notification;
  onDismiss: () => void;
  index: number;
  isRtl?: boolean;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onDismiss, index, isRtl = false }) => {
  const { t: tNotifications } = useTranslation('notifications');
  const { direction } = useAppLocale();
  const handleDismiss = () => {
    onDismiss();
  };

  const enterDelayMs = index * 100;

  const getNotificationStyles = () => {
    const baseStyles = 'relative z-10 notification-item-mobile notification-stack touch-target transition-all duration-300 ease-out';

    // Enhanced animation classes
    let animationClass = '';
    if (notification.isExiting) {
      animationClass = 'notification-exit';
    } else {
      animationClass = 'notification-enter';
    }

    switch (notification.type) {
      case 'success':
        return `${baseStyles} ${animationClass} bg-green-50/95 border-green-300 shadow-green-200/50`;
      case 'error':
        return `${baseStyles} ${animationClass} bg-red-50/95 border-red-300 shadow-red-200/50`;
      case 'warning':
        return `${baseStyles} ${animationClass} bg-yellow-50/95 border-yellow-300 shadow-yellow-200/50`;
      case 'info':
        return `${baseStyles} ${animationClass} bg-blue-50/95 border-blue-300 shadow-blue-200/50`;
      default:
        return `${baseStyles} ${animationClass} bg-gray-50/95 border-gray-300 shadow-gray-200/50`;
    }
  };

  const getIcon = () => {
    const iconClass = "w-5 h-5 flex-shrink-0";

    switch (notification.type) {
      case 'success':
        return (
          <svg className={`${iconClass} text-green-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className={`${iconClass} text-red-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      case 'warning':
        return (
          <svg className={`${iconClass} text-yellow-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'info':
        return (
          <svg className={`${iconClass} text-blue-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getTextColor = () => {
    switch (notification.type) {
      case 'success':
        return 'text-green-800';
      case 'error':
        return 'text-red-800';
      case 'warning':
        return 'text-yellow-800';
      case 'info':
        return 'text-blue-800';
      default:
        return 'text-gray-800';
    }
  };

  const showProgressBar =
    !notification.persistent && notification.duration != null && notification.duration > 0;

  const getProgressBarClass = () => {
    switch (notification.type) {
      case 'success':
        return 'bg-green-500';
      case 'error':
        return 'bg-red-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'info':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div
      dir={direction}
      className={`${getNotificationStyles()} w-full overflow-hidden sm:max-w-md sm:mx-auto`}
      style={
        notification.isExiting
          ? undefined
          : { animationDelay: `${enterDelayMs}ms` }
      }
    >
      <div className={`flex items-start gap-3 px-1 ${showProgressBar ? 'pb-1' : ''}`}>
        <div className="shrink-0 pt-0.5">
          {getIcon()}
        </div>
        <div className="min-w-0 flex-1 text-start">
          {notification.title && (
            <div className={`font-medium ${getTextColor()}`}>
              {notification.title}
            </div>
          )}
          {notification.message && (
            <div className={`${notification.title ? 'mt-1' : ''} text-sm ${getTextColor()}`}>
              {notification.message}
            </div>
          )}

          {notification.actions && notification.actions.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2 justify-start">
              {notification.actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.action}
                  className={`text-xs px-3 py-1 rounded transition-colors ${action.style === 'primary'
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : action.style === 'danger'
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                    }`}
                >
                  {action.label || action.persianLabel}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="relative z-10 shrink-0 text-gray-400 hover:text-gray-600 transition-colors touch-target-large flex items-center justify-center self-start"
          aria-label={tNotifications('dismiss')}
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {showProgressBar && (
        <div
          className="pointer-events-none absolute bottom-0 left-0 right-0 h-0.5 bg-black/5 dark:bg-white/10"
          aria-hidden
        >
          <div
            className={`h-full w-full ${getProgressBarClass()}`}
            style={{
              transformOrigin: isRtl ? 'right' : 'left',
              animation: `notification-toast-progress-scale ${notification.duration}ms linear forwards`,
            }}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Hook to use notification system
 */
export const useNotifications = () => {
  const context = useContext(NotificationSystemContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationSystem');
  }
  return context;
};

/**
 * Hook for form feedback
 */
export const useFormFeedback = () => {
  const { showSuccess, showError, showWarning } = useNotifications();
  const { t } = useTranslation('notifications');

  const showValidationError = useCallback((errors: Record<string, string[]>) => {
    const fields = Object.keys(errors);
    if (fields.length === 0) return;

    const firstField = fields[0];
    if (!firstField) return;

    const fieldErrors = errors[firstField];
    if (!fieldErrors || fieldErrors.length === 0) return;

    const firstError = fieldErrors[0];
    if (!firstError) return;

    showError(firstError, {
      context: 'form_validation'
    });
  }, [showError, t]);

  const showSubmissionSuccess = useCallback((message?: string) => {
    showSuccess(message || t('form.submitSuccess'), {
      context: 'form_submission'
    });
  }, [showSuccess, t]);

  const showSubmissionError = useCallback((message?: string) => {
    showError(message || t('form.submitFailed'), {
      context: 'form_submission'
    });
  }, [showError, t]);

  return {
    showValidationError,
    showSubmissionSuccess,
    showSubmissionError
  };
};

// CSS animations are defined in the main CSS file

export default NotificationSystem;